import {
    getClientSongCollectionTiktokAudioStats,
    getTiktokAudioAssets,
    TiktokAudioAsset,
    TrndsClientTiktokAudioStats,
} from '@round/api';
import { useCallback, useState } from 'react';
import useAbortableEffect from '../../../../Hooks/useAbortableEffect';

export type UseClientSongCollectionTiktokAudioStatsParams = Omit<
    Parameters<typeof getClientSongCollectionTiktokAudioStats>[0],
    'songCollectionId'
> & { songCollectionId: number | undefined };

export default function useClientSongCollectionTiktokAudioStats({
    clientId,
    songCollectionId,
    search,
    page,
    page_size,
    ordering,
}: UseClientSongCollectionTiktokAudioStatsParams) {
    const [isLoading, setIsLoading] = useState(false);
    const [hasErrorLoading, setHasErrorLoading] = useState(false);
    const [count, setCount] = useState(0);
    const [stats, setStats] = useState<TrndsClientTiktokAudioStats[]>([]);
    const [assets, setAssets] = useState<TiktokAudioAsset[]>([]);

    const fetchData = useCallback(
        async (requestInit?: RequestInit) => {
            if (!songCollectionId) {
                return;
            }

            try {
                setIsLoading(true);
                setHasErrorLoading(false);
                const response = await getClientSongCollectionTiktokAudioStats(
                    {
                        clientId,
                        songCollectionId,
                        search,
                        page,
                        page_size,
                        ordering,
                    },
                    requestInit
                );

                if (response.status !== 200) {
                    setHasErrorLoading(true);
                    return;
                }

                setStats(response.data.results);
                setCount(response.data.count);
                const audioIds = response.data.results.map((stats) => stats.audio_id);
                if (!audioIds.length) {
                    return;
                }

                const assets = await getTiktokAudioAssets(audioIds, requestInit);
                setAssets(assets);
            } catch (e) {
                if (e instanceof Error && e.name === 'AbortError') {
                    return;
                }

                setHasErrorLoading(true);
            } finally {
                setIsLoading(false);
            }
        },
        [clientId, ordering, page, page_size, search, songCollectionId]
    );

    useAbortableEffect(
        (signal) => {
            fetchData({ signal });
        },
        [fetchData]
    );

    return {
        isLoading,
        hasErrorLoading,
        count,
        stats,
        assets,
        refresh: fetchData,
        reset: () => {
            setStats([]);
            setAssets([]);
            setIsLoading(false);
            setHasErrorLoading(false);
            setCount(0);
        },
    };
}
